import { AfterViewInit, Component, OnInit } from '@angular/core';

import * as SimpleMDE from 'simplemde';

import { AccountService } from '../../../../../services/account/account.service';
import { LocationService } from '../../../../../services/location/location.service';
import {
  CapitanLocation,
  LocationCheckInRequiredProficienciesMatchMode,
  LocationDefaultStaffSiteEventsView,
  LocationSchedulerFirstDayOfWeek
} from '../../../../../models/location';
import { EventTypeService } from '../../../../../services/event-type/event-type.service';
import { dateStringFromISO8601 } from '../../../../../utils';
import { MyAccount } from '../../../../../models/my-account';
import { ApiService } from '../../../../../services/api/api.service';

@Component({
  selector: 'app-event-settings',
  templateUrl: './event-settings.component.html',
  styleUrls: ['./event-settings.component.css']
})
export class EventSettingsComponent implements OnInit, AfterViewInit {

  public selectedLocationId: number;
  public location: CapitanLocation;

  public blackoutDates: string[] = [];
  public newBlackoutDateInput = '';

  public eventSettingsErrorMessage = '';
  public blackoutDatesErrorMessage = '';
  public eventSettingsErrors: any = {};
  public eventSettingsSuccessMessage = '';
  public blackoutDatesSuccessMessage = '';

  public loadingEventSettings = false;
  public loadingBlackoutDates = false;
  public loadingEditBlackoutDates = false;

  public notFound = false;
  public serverError: number = null;

  // Create instance references to enums to they may be called in the template.
  public LocationSchedulerFirstDayOfWeek = LocationSchedulerFirstDayOfWeek;
  public LocationDefaultStaffSiteEventsView = LocationDefaultStaffSiteEventsView;
  public dateStringFromISO8601 = dateStringFromISO8601;

  public myAccount: MyAccount;

  private markdownEditor: any = null;

  constructor(
    public locationService: LocationService,
    public apiService: ApiService,
    private accountService: AccountService,
    private eventTypeService: EventTypeService
  ) {}

  ngOnInit() {
    this.myAccount = this.accountService.getMyAccount();

    this.selectedLocationId = this.myAccount.default_gym_id;

    if (this.myAccount.can_manage_events) {
      this.setInitialLocationValues();
      this.loadLocation();
      this.loadBlackoutDates();
    }
  }

  ngAfterViewInit(): void {
    this.markdownEditor = new SimpleMDE({
      element: document.getElementById('landingPageTextArea'),
      status: false,
      spellChecker: true,
      toolbar: [
        {
          name: 'bold',
          action: SimpleMDE.toggleBold,
          className: 'fa fa-bold',
          title: 'Bold',
          default: true
        },
        {
          name: 'italic',
          action: SimpleMDE.toggleItalic,
          className: 'fa fa-italic',
          title: 'Italic',
          default: true
        },
        {
          name: 'heading',
          action: SimpleMDE.toggleHeadingSmaller,
          className: 'fa fa-header',
          title: 'Heading',
          default: true
        },
        '|',
        {
          name: 'quote',
          action: SimpleMDE.toggleBlockquote,
          className: 'fa fa-quote-left',
          title: 'Quote',
          default: true
        },
        {
          name: 'unordered-list',
          action: SimpleMDE.toggleUnorderedList,
          className: 'fa fa-list-ul',
          title: 'Generic List',
          default: true
        },
        {
          name: 'ordered-list',
          action: SimpleMDE.toggleOrderedList,
          className: 'fa fa-list-ol',
          title: 'Numbered List',
          default: true
        },
        '|',
        {
          name: 'link',
          action: SimpleMDE.drawLink,
          className: 'fa fa-link',
          title: 'Create Link',
          default: true
        },
        {
          name: 'image',
          action: SimpleMDE.drawImage,
          className: 'fa fa-picture-o',
          title: 'Insert Image',
          default: true
        },
        {
          name: 'table',
          action: SimpleMDE.drawTable,
          className: 'fa fa-table',
          title: 'Insert Table'
        },
        '|',
        {
          name: 'preview',
          action: SimpleMDE.togglePreview,
          className: 'fa fa-eye no-disable',
          title: 'Toggle Preview',
          default: true
        },
        '|',
        {
          name: 'guide',
          action: 'https://simplemde.com/markdown-guide',
          className: 'fa fa-question-circle',
          title: 'Markdown Guide',
          default: true
        }
      ]
    });
  }

  /**
   * Set the initial values for the location object. These will be replaced
   * once the location loads.
   */
  public setInitialLocationValues() {
    this.location = {
      id: null,
      url: '',
      name: '',
      hide_setter: null,
      time_zone: '',
      scheduler_url_path: '',
      scheduler_landing_page_text: '',
      scheduler_first_day_of_week: LocationSchedulerFirstDayOfWeek.sunday,
      default_staff_site_events_view: LocationDefaultStaffSiteEventsView.scheduledEventsList,
      capacity: null,
      open_date: '',
      combine_same_day_check_ins: false,
      check_in_required_proficiency_ids: [],
      check_in_required_proficiency_names: [],
      check_in_required_proficiencies_match_mode: LocationCheckInRequiredProficienciesMatchMode.any,
      vat_info: '',
      allow_climber_app_home_location_selection: true,
      allow_climber_app_product_selection: true,
      today_local: '',
      lightspeed_location_id: '',
      square_location_id: '',
      accounting_connection_id: null,
      accounting_connection_name: null,
      accounting_connection_software: null,
      payment_processor_connection_id: null,
      payment_processor_connection_name: null,
      mailchimp_check_in_event_name: '',
      xero_tracking_category_name: '',
      xero_tracking_category_option_name: '',
      created_at: null,
      updated_at: null,
      areas_url: ''
    };
  }

  /**
   * Load the user's default location.
   */
  public loadLocation() {
    this.loadingEventSettings = true;

    this.locationService
      .getLocation(this.selectedLocationId)
      .then(location => {
        this.location = location;
        this.setMarkdownEditorValue();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventSettings = false; });
  }

  public loadBlackoutDates() {
    this.loadingBlackoutDates = true;

    this.eventTypeService
      .getBlackoutDates(this.selectedLocationId)
      .then(blackoutDates => { this.blackoutDates = blackoutDates; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingBlackoutDates = false; });
  }

  public onEventSettingsSubmit() {
    this.loadingEventSettings = true;

    this.eventSettingsErrorMessage = '';
    this.eventSettingsErrors = {};
    this.eventSettingsSuccessMessage = '';

    // This is needed because the markdown editor does not link directly to the
    // location object.
    if (this.markdownEditor) {
      this.location.scheduler_landing_page_text = this.markdownEditor.value();
    }

    this.locationService
      .updateLocationEventSettings(this.location)
      .then(updatedLocation => {
        this.location = updatedLocation;
        this.setMarkdownEditorValue();

        this.accountService.loadMyAccountInfo();

        this.eventSettingsSuccessMessage = 'Event settings updated successfully.';
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.eventSettingsErrors = reason.error;
          this.eventSettingsErrorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          this.eventSettingsErrorMessage = 'You do not have permission to modify these settings.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventSettings = false; });
  }

  public isLoading() {
    return (
      this.loadingEventSettings ||
      this.loadingBlackoutDates ||
      this.loadingEditBlackoutDates
    );
  }

  public removeBlackoutDate(blackoutDateIndex: number) {
    this.blackoutDatesErrorMessage = '';
    this.blackoutDatesSuccessMessage = '';

    if (!confirm('Are you sure you want to remove this blackout date?')) {
      return;
    }

    this.loadingEditBlackoutDates = true;

    this.eventTypeService
      .removeBlackoutDate(this.selectedLocationId, this.blackoutDates[blackoutDateIndex])
      .then(() => {
        this.blackoutDatesSuccessMessage = dateStringFromISO8601(this.blackoutDates[blackoutDateIndex]) + ' removed successfully.';
        this.blackoutDates.splice(blackoutDateIndex, 1);
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.blackoutDatesErrorMessage = reason.error;

          // The most common error is that the blackout date has already been
          // removed, so we will reload the blackout days.
          this.loadBlackoutDates();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEditBlackoutDates = false; });
  }

  public addBlackoutDate() {
    this.blackoutDatesErrorMessage = '';
    this.blackoutDatesSuccessMessage = '';

    this.loadingEditBlackoutDates = true;

    this.eventTypeService
      .addBlackoutDate(this.selectedLocationId, this.newBlackoutDateInput)
      .then(() => {
        this.blackoutDatesSuccessMessage = dateStringFromISO8601(this.newBlackoutDateInput) + ' added successfully.';
        this.loadBlackoutDates();
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.blackoutDatesErrorMessage = reason.error;

          // A common error is that the blackout date already exists, so we will
          // reload the blackout days in case it was edited in a separate tab.
          this.loadBlackoutDates();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEditBlackoutDates = false; });
  }

  public blackoutDateInputValid() {
    return (this.newBlackoutDateInput.split('-').length === 3);
  }

  public handleSelectLocation() {
    this.loadLocation();
    this.loadBlackoutDates();
  }

  /**
   * Set the value of the text in the markdown editor. This is needed because
   * the editor does not link directly to the location object.
   */
  private setMarkdownEditorValue() {
    if (this.location && this.markdownEditor) {
      this.markdownEditor.value(this.location.scheduler_landing_page_text);
    }
  }

}
